import { NamespacedStoreState } from '@Types/store.types';
import { createSelector } from '@reduxjs/toolkit';
import { moduleNamespace } from '@Store/config.store';

const parseNamespace = (state: NamespacedStoreState) => state[moduleNamespace];

const riskStoreSelector = createSelector(
	parseNamespace,
	state => state.RiskStore,
);

const templateApplySelector = createSelector(
	riskStoreSelector,
	state => state.templateApply,
);

export const selectIsTemplateApplying = createSelector(
	templateApplySelector,
	state => state.status === 'applying',
);

export const selectUnavailableComponents = createSelector(
	templateApplySelector,
	state => state.availability.filter(a => !a.available),
);

export const selectUnavailableComponentsForSection = createSelector(
	[
		templateApplySelector,
		(_state: NamespacedStoreState, section: 'lof' | 'cof') => section,
	],
	(templateApply, section) =>
		templateApply.availability.filter(
			a => !a.available && a.section === section,
		),
);

export const selectUnrecognisedValues = createSelector(
	templateApplySelector,
	state => state.unrecognisedValues,
);

// Best-effort scoping: `unrecognisedValues` entries only carry `componentName`
// (no `section`/`categoryName` — see riskTemplateApply.types.ts), so this joins
// against `availability` by name alone. If the same component name is used in
// both a LoF and a CoF category, its unrecognised values match both sections'
// name sets and the entry is (incorrectly) shown on both tabs. Fixing this
// properly requires the server to include section on unrecognisedValues
// (tracked under I360A-2704) — until then this is a known, accepted gap.
export const selectUnrecognisedValuesForSection = createSelector(
	[
		templateApplySelector,
		(_state: NamespacedStoreState, section: 'lof' | 'cof') => section,
	],
	(templateApply, section) => {
		const sectionComponentNames = new Set(
			templateApply.availability
				.filter(a => a.section === section)
				.map(a => a.componentName),
		);
		return templateApply.unrecognisedValues.filter(entry =>
			sectionComponentNames.has(entry.componentName),
		);
	},
);

export const selectUnrecognisedValuesForComponent = createSelector(
	[
		templateApplySelector,
		(
			_state: NamespacedStoreState,
			_section: 'lof' | 'cof',
			componentName: string,
		) => componentName,
	],
	(templateApply, componentName): string[] =>
		templateApply.unrecognisedValues.find(
			entry => entry.componentName === componentName,
		)?.values ?? [],
);

export const selectTemplateLabel = createSelector(
	templateApplySelector,
	state => state.templateLabel,
);
